import { authGet } from '../../utils/authFetch';
import { UIMemoObject, UIMemoObjectItem } from '../../model/UiShapes';
import { wmServerAddress } from '../../common/MetaSetting';
import { fetchHttpResponseHandler } from '../../util/UtilFunction';
import { handleError } from '../managers/errorHandler';

// MemoObject API的统一错误处理
const handleMemoObjectApiError = (error: any, operation: string): boolean => {
  // 错误处理交给Manager层，这里只返回false
  return false;
};

// 默认的UIMemoObject对象
const defaultUIMemoObject: UIMemoObject = {
  memoId: '',
  objects: []
};

// 默认的UIMemoObjectItem对象
const defaultUIMemoObjectItem: UIMemoObjectItem = {
  label: '',
  type: ''
};

// 数据转换函数 - 将API返回的MemoObject数据转换为UIMemoObject类型
export function convertMemoObjectToUIMemoObject(memoObject: any): UIMemoObject | null {
  // 如果memoObject为空，返回null而不是默认对象
  if (!memoObject) {
    return null;
  }
  
  // 执行转换
  const result = Object.assign({}, defaultUIMemoObject, memoObject);
  
  return result;
}

// 批量转换函数
export function convertMemoObjectsToUIMemoObjects(memoObjects: any[]): (UIMemoObject | null)[] {
  // 如果输入不是数组，返回空数组
  if (!Array.isArray(memoObjects)) {
    return [];
  }
  
  // 执行批量转换
  const results = memoObjects.map((memoObject) => {
    return convertMemoObjectToUIMemoObject(memoObject);
  });
  
  return results;
}

// 将单个memo object数据转换为UIMemoObject类型
export function convertToUIMemoObject(memoObjectData: any): UIMemoObject | null {
  if (!memoObjectData) {
    return null;
  }

  try {
    // 初始化UIMemoObject对象
    const uiMemoObject: UIMemoObject = {
      memoId: '',
      objects: []
    };

    // 处理API返回的包装格式（responseType/message/content结构）
    let actualData = memoObjectData;
    if (memoObjectData.content && typeof memoObjectData.content === 'object') {
      actualData = memoObjectData.content;
    }
    
    // 处理嵌套的memoObject结构（API返回格式）
    if (actualData.memoObject && typeof actualData.memoObject === 'object') {
      actualData = actualData.memoObject;
    }

    // 优先使用guid作为memoId
    if (actualData.guid) {
      uiMemoObject.memoId = actualData.guid;
    } else if (actualData.memoId) {
      uiMemoObject.memoId = actualData.memoId;
    } else if (memoObjectData.guid) {
      // 如果嵌套结构中没有找到，尝试原始数据
      uiMemoObject.memoId = memoObjectData.guid;
    } else if (memoObjectData.memoId) {
      uiMemoObject.memoId = memoObjectData.memoId;
    }

    // 处理content字符串中的JSON - 支持新的数据结构
    if (actualData.content && typeof actualData.content === 'string') {
      try {
        const parsedContent = JSON.parse(actualData.content);
        if (parsedContent) {
          // 检查解析后的content中是否有objects数组（新格式）
          if (parsedContent.objects && Array.isArray(parsedContent.objects)) {
            uiMemoObject.objects = convertNewFormatObjects(parsedContent.objects);
          }
        }
      } catch (parseError) {
        // 解析content JSON字符串失败，错误处理交给Manager层
      }
    }

    // 备选方案：直接处理objects数组（新格式）
    if (actualData.objects && Array.isArray(actualData.objects)) {
      uiMemoObject.objects = convertNewFormatObjects(actualData.objects);
    }

    // 如果以上都没有找到objects，尝试从原始数据中查找
    if (uiMemoObject.objects.length === 0 && memoObjectData.objects && Array.isArray(memoObjectData.objects)) {
      uiMemoObject.objects = convertNewFormatObjects(memoObjectData.objects);
    }

    // 如果仍然没有objects，尝试直接解析content对象
    if (uiMemoObject.objects.length === 0 && actualData.content && typeof actualData.content === 'object') {
      if (actualData.content.objects && Array.isArray(actualData.content.objects)) {
        uiMemoObject.objects = convertNewFormatObjects(actualData.content.objects);
      }
    }

    // 简化验证逻辑：只要有memoId就返回对象
    if (uiMemoObject.memoId) {
      return uiMemoObject;
    } else {
      // 最后尝试：如果输入数据本身有guid或memoId，直接使用
      const fallbackMemoId = memoObjectData.guid || memoObjectData.memoId || memoObjectData.id || '';
      if (fallbackMemoId) {
        return {
          memoId: fallbackMemoId,
          objects: uiMemoObject.objects
        };
      }
      
      return null;
    }
  } catch (error) {
    // 即使发生异常，也尝试返回一个基本对象（如果有可能）
    try {
      const fallbackMemoId = memoObjectData.guid || memoObjectData.memoId || memoObjectData.id || '';
      if (fallbackMemoId) {
        return {
          memoId: fallbackMemoId,
          objects: []
        };
      }
    } catch (e) {
      // 备用逻辑也失败，忽略错误
    }
    return null;
  }
}

// 转换新格式的对象数据（支持"人"、"事"、"物"、"概念"四种模型）
function convertNewFormatObjects(objects: any[]): any[] {
  if (!Array.isArray(objects)) {
    return [];
  }

  const convertedObjects = objects.map((obj, index) => {
    // 如果已经是正确的格式，直接返回
    if (obj.label && obj.type) {
      return obj;
    }
    
    // 处理新格式：{model: '人', attributes: [...]}
    if (obj.model) {
      return {
        label: obj.model, // 使用model作为label
        type: obj.model,  // 使用model作为type
        attributes: convertNewFormatAttributes(obj.attributes) // 转换属性格式
      };
    }
    
    // 处理旧格式：{object: '鲁迅', attributes: [...]}
    if (obj.object) {          
      // 从attributes中提取type信息
      let type = 'unknown';
      if (obj.attributes && Array.isArray(obj.attributes) && obj.attributes.length > 0) {
        // 尝试从attributes中提取type信息
        const firstAttribute = obj.attributes[0];
        if (firstAttribute.type) {
          type = firstAttribute.type;
        } else if (firstAttribute.attribute) {
          type = firstAttribute.attribute;
        }
      }
      
      return {
        label: obj.object,
        type: type,
        attributes: convertNewFormatAttributes(obj.attributes) // 转换属性格式
      };
    }
    
    // 如果无法识别格式，返回null表示数据无效
    handleError(new Error('convertNewFormatObjects: 无法识别的对象格式'), { context: 'memoObjectAPI', showToast: false });
    return null;
  });

  // 过滤掉null值，只返回有效的对象
  return convertedObjects.filter(obj => obj !== null);
}

// 转换新格式的属性数据
function convertNewFormatAttributes(attributes: any[]): any[] {
  if (!Array.isArray(attributes)) {
    return [];
  }

  return attributes.map((attr) => {
    // 如果已经是正确的格式，直接返回
    if (attr.attribute || attr.key) {
      return attr;
    }
    
    // 处理新格式：{key: '姓名', values: ['鲁迅'], valueType: '字符串'}
    if (attr.key) {
      return {
        attribute: attr.key,      // 使用key作为attribute
        key: attr.key,            // 保留key字段
        values: attr.values || attr.value || [],  // 支持values数组，兼容value字段
        valueType: attr.valueType || '字符串', // 支持valueType
        content: Array.isArray(attr.values) ? attr.values.join('、') : 
                 Array.isArray(attr.value) ? attr.value.join('、') : '' // 兼容旧格式
      };
    }
    
    // 如果无法识别格式，返回null表示数据无效
    handleError(new Error('convertNewFormatAttributes: 无法识别的属性格式'), { context: 'memoObjectAPI', showToast: false });
    return null;
  });
}

// 批量将API返回的memo objects数据转换为UIMemoObject列表
export function convertToUIMemoObjects(memoObjectsData: any[]): (UIMemoObject | null)[] {
  // 如果输入不是数组，返回空数组
  if (!Array.isArray(memoObjectsData)) {
    return [];
  }
  
  // 执行批量转换
  const results: (UIMemoObject | null)[] = [];
  
  for (let i = 0; i < memoObjectsData.length; i++) {
    const item = memoObjectsData[i];
    const convertedItem = convertToUIMemoObject(item);
    results.push(convertedItem);
  }
  
  return results;
}

// 获取memo的objects数据
export async function fetchMemoObject(memoId: string): Promise<UIMemoObject | null> {
  if (!memoId) {
    handleError(new Error('fetchMemoObject: memoId不能为空'), { context: 'memoObjectAPI', showToast: false });
    return null;
  }
  
  try {
    const response = await authGet(`${wmServerAddress}/object/${memoId}`);
    const result = await fetchHttpResponseHandler(response);
    
    if (result?.responseType === 1 && result.content) {
      return convertToUIMemoObject(result.content);
    }
    
    return null;
  } catch (error) {
    handleMemoObjectApiError(error, `获取memo ${memoId} 的objects`);
    return null;
  }
}

// Query keys
export const memoObjectKeys = {
  all: ['memoObjects'] as const,
  lists: () => [...memoObjectKeys.all, 'list'] as const,
  list: () => [...memoObjectKeys.lists()] as const,
  details: () => [...memoObjectKeys.all, 'detail'] as const,
  detail: (id: string) => [...memoObjectKeys.details(), id] as const,
  
  // 扩展的查询键，与tagKeys保持一致风格
  memoObjects: (memoId?: string) => [...memoObjectKeys.all, 'memo', memoId || ''] as const,
};